import React, { FC, useEffect, useState, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams, useLocation } from 'react-router-dom'
import { useEffectOnce } from 'react-use'
import { Tabs, Tab, LAYOUT } from 'transfix-ui/components/DeprecatedTabs'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { TopNav } from 'transfix-ui/components/TopNav'
import { Box } from 'transfix-ui/core'
import { StringParam } from 'use-query-params'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useMarketplaceContext } from 'utils/context/MarketplaceContext'
import { LoadDetailsInvoice } from 'apps/fleet-planner/components/load-details/quickbooks/LoadDetailsInvoice'
import { Stops } from 'apps/fleet-planner/components/load-details/stops/'
import { StopsSummary } from 'apps/fleet-planner/components/load-details/stops/StopsSummary'
import { DeleteLoadModal } from 'apps/fleet-planner/components/modals'
import { LOAD_TABS } from 'apps/fleet-planner/constants'
import { ShareLoadProvider } from 'apps/fleet-planner/context/ShareLoadContext'
import { useLoadTabs } from 'apps/fleet-planner/hooks'
import { useTodosApi } from 'apps/fleet-planner/hooks/useTodosApi'
import { obscurePostDeliveryStatus } from 'apps/fleet-planner/pages/loads/utils'
import 'apps/fleet-planner/scss/pages/_load-details.scss'
import * as Types from 'apps/fleet-planner/types'
import { fetchCustomers } from 'apps/fleet-planner/utils/actions/customers'
import { fetchDispatchers } from 'apps/fleet-planner/utils/actions/dispatchers'
import { fetchDrivers } from 'apps/fleet-planner/utils/actions/drivers'
import { fetchEquipment } from 'apps/fleet-planner/utils/actions/equipment'
import { getRecentFacilities } from 'apps/fleet-planner/utils/actions/facilities'
import {
  fetchLoadDetails,
  updateLoadStatus,
  updateLoad,
} from 'apps/fleet-planner/utils/actions/loadDetails'
import { getTotalDocumentsCount } from 'apps/fleet-planner/utils/api'
import { scrollToField } from 'apps/fleet-planner/utils/scrollToField'
import { loadDetailsSelector } from 'apps/fleet-planner/utils/selectors/load'
import { modalInfoSelector } from 'apps/fleet-planner/utils/selectors/modals'
import { Container } from 'components/layout'
import { Card, GlobalToast } from 'transfix-ui'
import { useQueryParams } from 'utils/hooks'
import { DISPLAY } from 'constants/display'
import { LoadNotFound } from '../atoms/LoadNotFound'
import { BookableShipmentSavedSearchPostTruckReminderModal } from '../bookable-shipment-saved-search/alert/BookableShipmentSavedSearchPostTruckReminderModal'
import { ShareLoadModal } from '../share-load/ShareLoadModal'
import { ShareLoadToastWrapper } from '../share-load/ShareLoadToastWrapper'
import { ShareLoadViaSmsModal } from '../share-load/modals/shareLoadViaSms/ShareLoadViaSmsModal'
import { SmsPromptSettingsModal } from '../share-load/modals/smsPromptSettings/SmsPromptSettingsModal/SmsPromptSettingsModal'
import { CheckinCheckOutTimesModal } from './CheckinCheckoutTimesModal'
import { LoadDetailsAlerts } from './LoadDetailsAlerts'
import { LoadDetailsDocuments } from './LoadDetailsDocuments'
import { LoadDetailsHeader } from './LoadDetailsHeader'
import { LoadDetailsInlineEdit } from './LoadDetailsInlineEdit'
import { LoadDetailsReloadMessage } from './LoadDetailsReloadsMessage'
import { LoadNotesInlineEdit } from './LoadNotesInlineEdit'
import { LoadStatus } from './LoadStatus'
import { PickupEtaModal } from './PickupEtaModal'
import { LoadActivity } from './activity/LoadActivity'

export const LoadDetails: FC = () => {
  const dispatch = useDispatch()
  const { isMobile } = useBreakpointContext()

  const { pathname = '' } = useLocation()
  const history = useHistory()
  const { action, id } = useParams<{
    id: string
    action: string
  }>()
  const modalLoadId = id === 'create-load' ? undefined : id
  const [inlineEditField, setInlineEditField] = useState<string>()

  const EMPTY_OBJECT = {}

  const { actionInProgress: saving, error: modalError } = useSelector(modalInfoSelector)

  const { error: apiError, load, loading, permissions } = useSelector(loadDetailsSelector)
  const showNotFoundPage = pathname.includes('/load/not-found')

  const { drivers, stops } = load || []
  const {
    id: loadId,
    notes,
    motorCarrier,
    dispatcher,
    truck,
    trailer,
    status: loadStatus,
    currentStopSequence,
    externalId,
    rate,
    miles,
    trailerType,
    trailerLength,
    customer,
    customerId,
    customerContact,
    customerContactId,
    temperatureSetting,
    externalEntities,
    isExternal,
    instructionsFromCustomer,
    securingOptions,
    customerNotes,
    subscribers = [],
    settings,
    quickbooksInvoiceId,
  } = load || {}

  const status = obscurePostDeliveryStatus(loadStatus, isExternal)

  const {
    getMarketplaceLoad,
    shipmentDetails,
    updateError: externalUpdateError,
    updateTractorVin,
  } = useMarketplaceContext()

  const { findTodoByExternalId, getTodos, isLoading: isTodosLoading } = useTodosApi()

  useEffectOnce(() => {
    if (!isTodosLoading) {
      getTodos()
    }
  })

  const currentShipmentTodos = findTodoByExternalId(externalId || '')

  const [{ focusInput }] = useQueryParams({
    focusInput: StringParam,
  })

  /**
   * Fetch load details and clear inline edit field
   * when id changes
   */
  useEffect(() => {
    if (id) {
      setInlineEditField(focusInput)
      dispatch(fetchLoadDetails(id))
    }
  }, [dispatch, id, focusInput])

  /**
   * Fetch drivers and equipment on mount
   */
  useEffect(() => {
    dispatch(getRecentFacilities({ pageSize: 1000 }))
    dispatch(fetchCustomers())
    dispatch(fetchDrivers())
    dispatch(fetchEquipment())
    dispatch(fetchDispatchers())
  }, [dispatch])

  /**
   * In the case that an invalid id or route is given (404), display the Load not found page
   */
  useEffect(() => {
    if (apiError?.includes('Error: API route not found.')) {
      history.push('/load/not-found')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiError])

  const { activeTab, setActiveTab } = useLoadTabs()

  const onTodoClick = useCallback((name?: string, className?: string) => {
    scrollToField(className)
    setInlineEditField(name)
  }, [])

  const updateStatusHandler = useCallback(
    ({
      currentStopSequence: newCurrentStopSequence,
      status: newStatus,
    }: Types.ILoadStatusOption) => {
      dispatch(updateLoadStatus(load, newStatus, newCurrentStopSequence))
    },
    [dispatch, load]
  )

  const updateLoadHandler = useCallback(
    (changes: Partial<Types.ILoad>) => {
      dispatch(updateLoad({ ...load, ...changes }))
    },
    [dispatch, load]
  )

  const handleCloseModal = useCallback(() => {
    history.push(pathname.replace(/(\/[^/]+)$/, ''))
  }, [history, pathname])

  const errors = modalError || externalUpdateError
  const { shipperInsuranceRequirement = [], carrierPayment, info, stops: marketplaceStops } =
    shipmentDetails || {}
  const { bills, carrierRate, carrierTotalAccessorialRate, paymentTerms, quickPayEnabled, total } =
    carrierPayment || {}
  const { vin } = info || {}

  const updateTractorVinHandler = useCallback(
    (tractorVin: string) => {
      if (loadId && isExternal && externalId) {
        updateTractorVin(loadId, externalId, tractorVin)
      }
    },
    [externalId, isExternal, loadId, updateTractorVin]
  )

  // Fetch shipment details if load has been synced with Transfix.
  useEffect(() => {
    if (isExternal && externalId) {
      getMarketplaceLoad(externalId)
    }
  }, [externalId, getMarketplaceLoad, isExternal])

  const renderLoadStatus = () => (
    <LoadStatus
      onTractorVinUpdate={updateTractorVinHandler}
      tractorVinNumber={vin}
      isExternal={isExternal}
      externalId={externalId}
      externalEntities={externalEntities}
      onStatusUpdate={updateStatusHandler}
      updateLoad={updateLoadHandler}
      drivers={drivers}
      carrier={motorCarrier}
      truck={truck || EMPTY_OBJECT}
      trailer={trailer || EMPTY_OBJECT}
      dispatcher={dispatcher}
      status={status}
      stops={stops}
      currentStopSequence={currentStopSequence}
      inlineEditField={inlineEditField}
      setInlineEditField={setInlineEditField}
      subscribers={subscribers}
      customerContact={customerContact}
      settings={settings}
      currentShipmentTodos={currentShipmentTodos}
      bills={bills}
    />
  )

  const totalAttachmentsCount = useMemo(
    () =>
      getTotalDocumentsCount({
        fpLoad: load,
        mpShipment: shipmentDetails,
      }),
    [load, shipmentDetails]
  )

  if (showNotFoundPage) {
    return <LoadNotFound />
  }

  return (
    <>
      <GlobalToast />
      <TopNav title={`Load # ${externalId || DISPLAY.empty}`}>
        <LoadDetailsHeader load={load} isExternal={isExternal} />
      </TopNav>
      {errors && (
        <div className='row pa4'>
          <InlineMessage message={errors} type={INLINE_MESSAGE_VARIANTS.ALERT} />
        </div>
      )}
      <Container hidePadding>
        <section className='load-details-container'>
          {/* Overview + Summary */}
          <div className='load-details-overview'>
            <LoadDetailsAlerts
              onTodoClick={onTodoClick}
              onPODUpload={setActiveTab}
              currentShipmentTodos={currentShipmentTodos}
            />
            <StopsSummary stops={stops} />
          </div>
          {/* Tabs */}
          <div className='load-details-tabs'>
            <Tabs value={activeTab} onChange={setActiveTab} layout={LAYOUT.scrollable}>
              <Tab dataTest='details-tab' label={LOAD_TABS.loadDetails}>
                <Box p={2}>
                  {isMobile && <div className='tab-load-details-status'>{renderLoadStatus()}</div>}
                  <LoadDetailsReloadMessage stops={stops} />
                  <div className='tab-load-details-inline-edit'>
                    <LoadDetailsInvoice
                      status={status}
                      customer={customer}
                      id={loadId}
                      quickbooksInvoiceId={quickbooksInvoiceId}
                      rate={rate}
                    />
                    <LoadDetailsInlineEdit
                      carrierTotalAccessorialRate={carrierTotalAccessorialRate}
                      isQuickpayEnabled={quickPayEnabled}
                      paymentTerms={paymentTerms}
                      carrierRate={carrierRate}
                      totalRate={total}
                      securingOptions={securingOptions}
                      isExternal={isExternal}
                      customerNotes={customerNotes}
                      permissions={permissions}
                      externalId={externalId}
                      rate={rate}
                      miles={miles}
                      trailerType={trailerType}
                      trailerLength={trailerLength}
                      customer={customer}
                      customerId={customerId}
                      customerContact={customerContact}
                      customerContactId={customerContactId}
                      temperatureSetting={temperatureSetting}
                      onEditModeRequested={setInlineEditField}
                      currentEditField={inlineEditField}
                      onUpdate={updateLoadHandler}
                    />
                  </div>
                  {!loading && (
                    <div className='tab-load-details-stop'>
                      <Stops
                        status={status}
                        marketplaceStops={marketplaceStops}
                        isExternal={isExternal}
                        permissions={permissions}
                        loadId={id}
                        stops={stops}
                        onEditModeRequested={setInlineEditField}
                        currentEditField={inlineEditField}
                        updateLoad={updateLoadHandler}
                      />
                    </div>
                  )}

                  <div className='tab-load-details-notes'>
                    <LoadNotesInlineEdit
                      shipperInsuranceRequirement={shipperInsuranceRequirement}
                      instructionsFromCustomer={instructionsFromCustomer}
                      isExternal={isExternal}
                      onEditModeRequested={setInlineEditField}
                      currentEditField={inlineEditField}
                      notesText={notes}
                      updateLoad={updateLoadHandler}
                    />
                  </div>
                </Box>
              </Tab>
              <Tab dataTest='comments-tab' label={LOAD_TABS.comments}>
                <div />
              </Tab>
              <Tab label={LOAD_TABS.activity}>
                <div className='ph2 ph0-m'>
                  <LoadActivity loadId={load.id} loadStops={stops} isExternal={isExternal} />
                </div>
              </Tab>
              <Tab
                dataTest='attachments-tab'
                label={LOAD_TABS.documents}
                count={totalAttachmentsCount}>
                <Box p={2}>
                  <LoadDetailsDocuments />
                </Box>
              </Tab>
            </Tabs>
          </div>
          {/* Load Status */}
          {!isMobile && (
            <div className='load-details-sidebar'>
              <Card>{renderLoadStatus()}</Card>
            </div>
          )}
        </section>
      </Container>
      {action === 'delete' && (
        <DeleteLoadModal id={load.id} showDeleteModal saving={saving} error={modalError} />
      )}
      {action === 'share-load-via-sms' && <ShareLoadViaSmsModal open onClose={handleCloseModal} />}
      {action === 'sms-prompt-settings' && (
        <SmsPromptSettingsModal open onClose={handleCloseModal} />
      )}
      {action === 'post-truck' && (
        <BookableShipmentSavedSearchPostTruckReminderModal stops={stops} />
      )}
      {action === 'update-status' && (
        <CheckinCheckOutTimesModal load={load} isOpen onClose={handleCloseModal} />
      )}
      {action === 'update-eta' && <PickupEtaModal load={load} isOpen onClose={handleCloseModal} />}
      {action === 'share' && (
        <ShareLoadProvider>
          <ShareLoadToastWrapper />
          <ShareLoadModal loadId={modalLoadId} isOpen onClose={handleCloseModal} />
        </ShareLoadProvider>
      )}
    </>
  )
}
